<script setup>
import { computed } from "vue";
import { useAuthStore } from "../../pinia/auth.ts";

const authStore = useAuthStore();

const props = defineProps({
  theme: {
    type: String,
    required: false,
    default: null,
  },
  highContrast: {
    type: Boolean,
    required: false,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
  ariaLabel: {
    type: String,
    default: null,
  },
});

// Use passed props or fall back to auth store
const activeTheme = computed(() => props.theme ?? authStore.theme?.Accent);
const activeHighContrast = computed(() => props.highContrast ?? authStore.theme?.HighAccessibility);

const themeClass = computed(() => {
  const theme = activeTheme.value;
  const hc = activeHighContrast.value;

  if (!theme) {
    return "theme-default";
  }

  return `theme-${theme}${hc ? "-hc" : ""}`;
});
</script>

<template>
  <button
    :aria-label="ariaLabel"
    :class="[themeClass, { 'themed-button--disabled': disabled }]"
    :disabled="disabled"
    :type="type"
    class="themed-button"
    v-bind="$attrs"
  >
    <slot />
  </button>
</template>

<style scoped>
.themed-button {
  @apply inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border-0 px-3 text-sm font-medium text-primary-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50;
}

.themed-button--disabled {
  @apply pointer-events-none opacity-50;
}

.theme-teal {
  @apply bg-theme-teal hover:bg-theme-teal/90 focus-visible:outline-theme-teal;
}

.theme-teal-hc {
  @apply bg-theme-teal-hc hover:bg-theme-teal-hc/90 focus-visible:outline-theme-teal-hc;
}

.theme-grape {
  @apply bg-theme-grape hover:bg-theme-grape/90 focus-visible:outline-theme-grape;
}

.theme-grape-hc {
  @apply bg-theme-grape-hc hover:bg-theme-grape-hc/90 focus-visible:outline-theme-grape-hc;
}

.theme-iceberg {
  @apply bg-theme-iceberg hover:bg-theme-iceberg/90 focus-visible:outline-theme-iceberg;
}

.theme-iceberg-hc {
  @apply bg-theme-iceberg-hc hover:bg-theme-iceberg-hc/90 focus-visible:outline-theme-iceberg-hc;
}

.theme-rose {
  @apply bg-theme-rose hover:bg-theme-rose/90 focus-visible:outline-theme-rose;
}

.theme-rose-hc {
  @apply bg-theme-rose-hc hover:bg-theme-rose-hc/90 focus-visible:outline-theme-rose-hc;
}

.theme-offsite {
  @apply bg-theme-offsite hover:bg-theme-offsite/90 focus-visible:outline-theme-offsite;
}

.theme-offsite-hc {
  @apply bg-theme-offsite-hc hover:bg-theme-offsite-hc/90 focus-visible:outline-theme-offsite-hc;
}

.theme-default {
  @apply bg-theme-grape hover:bg-theme-grape/90 focus-visible:outline-theme-grape;
}
</style>
